import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Ban, Flag, Archive, ShieldCheck, Loader2, Undo2 } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { useI18n } from "../../i18n";

/**
 * P2/P3 — owner-facing protection actions for ONE conversation.
 * Every decision (authorization, scope, persistence) happens in the backend
 * `/api/protection/*` contract; this component only renders the outcome.
 */

const API = "/api";
const REASONS = ["harassment", "threats", "spam", "inappropriate", "not_relevant", "other"];

export const ConversationProtection = ({ detail, onDone }) => {
  const { t } = useI18n();
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState("harassment");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const blocked = detail.status === "blocked";

  const run = async (path, body, okKey) => {
    setBusy(true);
    try {
      await axios.post(`${API}/protection/conversations/${detail.id}/${path}`, body || {});
      toast.success(t(`app.protection.${okKey}`));
      setDialog(null);
      setNote("");
      onDone?.();
    } catch (_e) {
      toast.error(t("app.protection.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1" data-testid="conversation-protection-actions">
      {blocked ? (
        <button
          onClick={() => run("unblock", null, "unblocked_ok")}
          disabled={busy}
          className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-[#0B2545] transition-colors hover:bg-slate-100"
          data-testid="conversation-unblock-btn"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
          {t("app.protection.unblock")}
        </button>
      ) : (
        <>
          <button
            onClick={() => setDialog("block")}
            className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-red-700"
            title={t("app.protection.block")}
            aria-label={t("app.protection.block")}
            data-testid="conversation-block-btn"
          >
            <Ban className="h-4 w-4" />
          </button>
          <button
            onClick={() => setDialog("report")}
            className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-amber-700"
            title={t("app.protection.report")}
            aria-label={t("app.protection.report")}
            data-testid="conversation-report-btn"
          >
            <Flag className="h-4 w-4" />
          </button>
          {detail.status === "active" && (
            <button
              onClick={() => run("close", null, "closed_ok")}
              disabled={busy}
              className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-[#0B2545]"
              title={t("app.protection.close")}
              aria-label={t("app.protection.close")}
              data-testid="conversation-close-btn"
            >
              <Archive className="h-4 w-4" />
            </button>
          )}
        </>
      )}

      <Dialog open={dialog !== null} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid={`conversation-${dialog}-dialog`}>
          <DialogHeader>
            <DialogTitle>
              {dialog === "report" ? t("app.protection.report") : t("app.protection.block")}
            </DialogTitle>
            <DialogDescription>
              {dialog === "report"
                ? t("app.protection.report_explain")
                : t("app.protection.block_explain")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <label className="block text-sm font-semibold text-[#0B2545]" htmlFor="protection-reason">
              {t("app.protection.reason_label")}
            </label>
            <select
              id="protection-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-[#0B2545]"
              data-testid="protection-reason-select"
            >
              {REASONS.map((r) => (
                <option key={r} value={r}>{t(`app.protection.reason.${r}`)}</option>
              ))}
            </select>
            <label className="block text-sm font-semibold text-[#0B2545]" htmlFor="protection-note">
              {t("app.protection.details_label")}
            </label>
            <textarea
              id="protection-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("app.protection.details_ph")}
              className="w-full resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm text-[#0B2545] outline-none focus:border-[#1B84FF]"
              data-testid="protection-details-input"
            />
            <p className="flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-700">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#1B84FF]" />
              {t("app.protection.scope_note")}
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialog(null)}
              className="border-slate-300 text-[#0B2545]"
              data-testid="protection-cancel-btn"
            >
              {t("app.protection.cancel")}
            </Button>
            <Button
              disabled={busy}
              onClick={() => (dialog === "report"
                ? run("report", { reason_code: reason, details: note }, "reported_ok")
                : run("block", { reason_code: reason, details: note }, "blocked_ok"))}
              className="bg-[#1B84FF] text-white hover:bg-[#0F6FDB]"
              data-testid="protection-confirm-btn"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {dialog === "report" ? t("app.protection.confirm_report") : t("app.protection.confirm_block")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConversationProtection;
